import { Box, Button, CircularProgress, Grid, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import React, { useState } from "react";
import DropdownMenu from "common/DropDownMenu/DropDownMenu";
import FormDialog from "common/Modal/Modal";
import { useImportService } from "subModule/src/services/allcommon/useCommon";
import { enqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { CommonProfileService } from "subModule/src/core/services";
import { ImportResponse } from "@subModule/src/services/allcommon/interface";
import { UploadPreviewTable } from "common/Misc/UploadPreviewTable";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { useSelector } from "react-redux";
import { selectUserInfo } from "features/user/userSlice";

type ItemSearchHeaderMenuProps = {
  refetch?: () => void;
};

const ItemSearchHeaderMenu: React.FC<ItemSearchHeaderMenuProps> = ({
  refetch,
}) => {
  const [modal, setModal] = useState<boolean>(false);
  const { t } = useTranslation();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const loggedInUserData = useSelector(selectUserInfo);
  const primaryLanguage = loggedInUserData?.business?.primaryLanguage;
  const secondaryLanguage = loggedInUserData?.business?.secondaryLanguage;
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [showPreview, setShowPreview] = useState<boolean>(false);
  const [imports, setImports] = useState<any>([]);
  const [fileName, setFileName] = useState<string>("");
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setFileName(file ? file.name : "");
  };
  const closeModal = () => {
    setModal(false);
    setFileName("");
    setShowPreview(false);
    setImports([]);
  };
  const columns = [
    { id: "category", label: t("category"), numeric: false },
    { id: "itemCode", label: t("itemCode"), numeric: false },
    {
      id: "title",
      label: `${!primaryLanguage ? t("primary") : ""} ${t("title")} ${primaryLanguage ? ` (${t(primaryLanguage)})` : ""}`,
      numeric: false,
    },
    {
      id: "secondaryTitle",
      label: `${!secondaryLanguage ? t("secondary") : ""} ${t("title")} ${secondaryLanguage ? ` (${t(secondaryLanguage)})` : ""}`,
      numeric: false,
    },
    { id: "partNo", label: t("partNo"), numeric: false },
    { id: "manufacturerTitle", label: t("manufacturer"), numeric: false },
    { id: "unitTitle", label: t("unitTitle"), numeric: false },
    { id: "itemGroupTitle", label: t("itemGroup"), numeric: false },
    { id: "itemCategoryTitle", label: t("itemCategory"), numeric: false },
    { id: "taxable", label: t("taxable"), numeric: false },
    { id: "taxProcedureTitle", label: t("taxProcedure"), numeric: false },
    { id: "price", label: t("price"), numeric: false },
    { id: "depreciation", label: t("depreciation"), numeric: false },
    { id: "haveScrapItem", label: t("haveScrapItem"), numeric: false },
    { id: "scrapItemTitle", label: t("scrapItem"), numeric: false },
    {
      id: "stockReferanceNumber",
      label: t("stockReferanceNumber"),
      numeric: false,
    },
    { id: "storeTitle", label: t("stores"), numeric: false },
    { id: "itemCost", label: t("itemCost"), numeric: false },
    { id: "stockQuantity", label: t("Quantity"), numeric: false },
    {
      id: "itemSupplierNumber",
      label: t("itemSupplierNumber"),
      numeric: false,
    },
    // { id: 'warranty', label: t('warranty') + ' ' + t('days'), numeric: false },
    {
      id: "warrantyMileage",
      label: t("warranty") + " " + t("mileage"),
      numeric: false,
    },
    {
      id: "itemWarrantyServiceId",
      label: t("itemWarrantyServiceId"),
      numeric: false,
    },
    { id: "sellBelowCost", label: t("sellBelowCost"), numeric: false },
    { id: "shippingMethod", label: t("shippingMethod"), numeric: false },
    { id: "itemEveryMile", label: t("Mileage (km)"), numeric: false },
    { id: "itemEveryPeriodType", label: t("Number of Days"), numeric: false },
  ];
  const onExport = async () => {
    setDownloading(true);
    CommonProfileService.exportEntity({ typeName: "items/export/sample" })
      .then((res: Blob) => {
        const blob = new Blob([res], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `ItemsSample.xlsx`);
        document.body.appendChild(link);
        link.click();

        link.remove();
        window.URL.revokeObjectURL(url);
        enqueueSnackbar(
          <AlertMessages text={t("sampleDownloadedSuccessfully")} />,
          { variant: "success" },
        );
      })
      .catch((err) => {
        console.error("===", err);
        enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: "error" });
      })
      .finally(() => {
        setDownloading(false);
      });
  };

  const { onImport } = useImportService({
    onSuccess: (msg) => {
      const response: any = {};
      Object.keys(msg).forEach((key, index) => {
        response[key] = Object.values(msg)[index];
      });
      if (response.success) {
        setLoading(false);
        setShowPreview(true);
        setImports(response?.data);
      } else {
        enqueueSnackbar(<AlertMessages text={response.message} />, {
          variant: "error",
        });
        setLoading(false);
      }
    },
    onError: (err: any) => {
      enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: "error" });
      if (err instanceof Error) {
        console.error(err.stack);
      }
      setLoading(false);
    },
  });
  const Import = () => {
    setLoading(true);
    CommonProfileService.importEntity(imports, "items")
      .then((response: ImportResponse) => {
        if (response.success) {
          refetch && refetch();
          enqueueSnackbar(
            <AlertMessages
              text={
                response?.totalAdded +
                " " +
                t("added") +
                ", " +
                response?.totalFailed +
                " " +
                t("omitted")
              }
            />,
            { variant: "success" },
          );
          closeModal();
        } else {
          enqueueSnackbar(<AlertMessages text={response?.message} />, {
            variant: "error",
          });
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        enqueueSnackbar(
          <AlertMessages
            text={err?.message || err?.data?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
      })
      .finally(() => setLoading(false));
  };

  return (
    <Box>
      <Button
        id="import-button"
        variant="contained"
        disableElevation
        onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
          setAnchorEl(event.currentTarget)
        }
        endIcon={<KeyboardArrowDownIcon />}
        className={`bg-white font-Saira font-[600] text-[13px] px-4 py-1 cursor-pointer rounded-[6px]`}
        sx={{
          outline: "2px solid",
          color: themeColors?.primary,
          borderColor: themeColors?.primary,
          height: 34,
          minHeight: 34,
        }}
      >
        {downloading ? (
          <CircularProgress sx={{ color: themeColors?.primary }} size={24} />
        ) : (
          t("import")
        )}
      </Button>
      <DropdownMenu
        anchorEl={anchorEl}
        menuItems={[
          {
            label: t("Download Sample") + " " + t("(Excel)"),
            onClick: () => onExport(),
          },
          {
            label: t("Upload") + " " + t("(Excel)"),
            onClick: () => setModal(true),
          },
        ]}
        onClose={() => setAnchorEl(null)}
        hoverColor={themeColors?.primary}
      />
      {modal && (
        <FormDialog
          open={modal}
          onClose={(event: any, reason: any) => {
            if (event && reason !== "backdropClick" && !loading) closeModal();
          }}
          title={(showPreview ? t("Imported") : t("Upload")) + " " + t("items")}
          createPopup={true}
          hideActions={true}
          fullWidth={showPreview}
          content={
            <Grid
              container
              className="items-center"
              component="form"
              noValidate={false}
              spacing={1}
              onSubmit={(e) => {
                e.preventDefault();
                setLoading(true);
                const data = new FormData(e.target as HTMLFormElement);
                const file = data.get("file") as File;
                onImport({
                  file: file,
                  typeName: "items",
                });
              }}
            >
              {!showPreview ? (
                <>
                  <Grid item xs={12} md={8} id="importfile">
                    <input
                      type="file"
                      name="file"
                      required
                      accept=".xls,.xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                      style={{ color: fileName ? "inherit" : "#fff" }}
                      onChange={handleFileChange}
                      title={fileName}
                      // onInvalid={(e) => (e.target as HTMLInputElement).setCustomValidity(t('uploadFileRequired'))}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      type="submit"
                      disabled={loading}
                      margin="dense"
                      value={t("Upload")}
                      InputProps={{
                        startAdornment: loading ? (
                          <CircularProgress
                            size={20}
                            sx={{
                              color: themeColors?.primary,
                              position: "absolute",
                              left: "10px",
                            }}
                          />
                        ) : null,
                      }}
                      sx={{
                        "& div": {
                          borderRadius: "8px",
                          border: "0 !important",
                        },
                        ["& input"]: {
                          fontFamily: "'Saira', sans-serif",
                          fontSize: "14px",
                          lineHeight: "28px",
                          fontWeight: 600,
                          color: "#fff",
                          padding: "0.52rem 2.22rem 0.6rem 2.22rem",
                          boxShadow:
                            "0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)",
                          backgroundImage: `linear-gradient(135deg, ${themeColors?.secondary}, ${themeColors?.secondary + "B3"})`,
                          border: "0 !important",
                          borderRadius: "8px",
                          cursor: "pointer",
                          textTransform: "uppercase",
                          height: "25px",
                        },
                        "& .MuiInputBase-root": { padding: 0 },
                      }}
                    />
                  </Grid>
                </>
              ) : null}
              {showPreview ? (
                <>
                  <Grid item xs={12}>
                    <UploadPreviewTable rows={imports} columns={columns} />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    id="form"
                    className="flex justify-end gap-2"
                  >
                    <Button
                      className="secondary"
                      style={{ color: themeColors?.primary }}
                      variant="contained"
                      disableElevation
                      onClick={() => closeModal()}
                    >
                      {t("cancel")}
                    </Button>
                    <Button
                      disabled={loading}
                      className="primary"
                      style={{ background: themeColors?.primary }}
                      variant="contained"
                      disableElevation
                      onClick={() => Import()}
                    >
                      {loading && (
                        <CircularProgress
                          size={15}
                          sx={{
                            color: "#fff",
                            position: "absolute",
                            left: "5px",
                          }}
                        />
                      )}
                      {t("submit")}
                    </Button>
                  </Grid>
                </>
              ) : null}
            </Grid>
          }
        />
      )}
    </Box>
  );
};

export default ItemSearchHeaderMenu;
